import {
    NetworkDetailResponse,
    NetworkListResponse,
    PaginationTypeResponse,
    PartnerListResponse,
    RemoveResponse,
    ResponseType,
    TranslationResponse,
    TypeSafeResponseType,
} from '../api/types';
import { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from '../contexts/TranslationContext';
import { useNavigate } from 'react-router-dom';
import { PartnerDetailResponse } from '../api/types/partner';
import {
    CronjobDetailResponse,
    CronjobListResponse,
    CronjobLogResponse,
    CronjobStartedResponse,
    CronjobStoppedResponse,
} from '../api/types/cronjob';
import { FormConfigResponse } from '../api/types/form';
import {
    FileDeleteResponse,
    FileListResponse,
    UploadedFileResponse,
} from '../api/types/cms';
import { useSelector } from 'react-redux';
import { SettingsState, Store } from '../store/types';
import { FormErrors } from '../components/form/types';
import { buildObjectFromKeyValue } from './static';
import {
    ApartmentDetailResponse,
    DistrictDetailResponse,
    DistrictListResponse,
    PropertyDetailResponse,
    PropertyListResponse, ReportTopicDetailResponse, ReportTopicListResponse,
} from '../api/types/property';

export type AnyResponse =
    | ResponseType
    | TypeSafeResponseType<any>
    | RemoveResponse
    | FormConfigResponse
    | TranslationResponse
    | PaginationTypeResponse
    | FileListResponse
    | FileDeleteResponse
    | UploadedFileResponse
    | NetworkListResponse
    | NetworkDetailResponse
    | PartnerListResponse
    | PartnerDetailResponse
    | CronjobListResponse
    | CronjobDetailResponse
    | CronjobStartedResponse
    | CronjobLogResponse
    | CronjobStoppedResponse
    | DistrictListResponse
    | DistrictDetailResponse
    | PropertyListResponse
    | PropertyDetailResponse
    | ApartmentDetailResponse
    | ReportTopicListResponse
    | ReportTopicDetailResponse
;

export const useResponseHandler = () => {
    const navigate = useNavigate();
    const { translate } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { redirectAfterSave } = useSelector<Store, SettingsState>(
        state => state.settings,
    );

    return useMemo(() => {
        const navigateBack = (force: boolean = false) => {
            if (redirectAfterSave || force) {
                navigate(-1);
            }
        };

        const handleResponse = (
          response: AnyResponse | null,
          onSuccess: () => void,
          onError: (messages: FormErrors) => void = () => {},
        ) => {
            if (response && !response.error) {
                onSuccess();
            } else {
                switch (response?.status || response?.code) {
                    case 400:
                        enqueueSnackbar(translate('errors.bad_request'), {
                            variant: 'error',
                            disableWindowBlurListener: true,
                        });
                        break;
                    case 401:
                        enqueueSnackbar(translate('errors.unauthorized'), {
                            variant: 'error',
                            disableWindowBlurListener: true,
                        });
                        break;
                    case 403:
                        enqueueSnackbar(translate('errors.access_denied'), {
                            variant: 'error',
                            disableWindowBlurListener: true,
                        });
                        break;
                    case 404:
                        enqueueSnackbar(translate('errors.not_found'), {
                            variant: 'error',
                            disableWindowBlurListener: true,
                        });
                        setTimeout(navigateBack, 2000);
                        break;
                    default:
                        enqueueSnackbar(translate('errors.unknown'), {
                            variant: 'unknownError',
                            disableWindowBlurListener: true,
                            response,
                        });
                        break;
                }
                if (onError && response?.messages) {
                    onError(buildObjectFromKeyValue(response?.messages));
                }
            }
        };

        return {
            navigateBack,
            handleResponse,
        };
    }, [redirectAfterSave, navigate, enqueueSnackbar, translate]);
};
